import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, Inbox } from 'lucide-react';
import { cn } from './cn';
import { ApiError } from '@/lib/api';
import { Button } from './Button';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 aria-hidden className={cn('size-4 animate-spin text-text-subtle', className)} />;
}

export function LoadingBlock({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-2 py-10 text-sm text-text-muted', className)}>
      <Spinner />
      <span>{label}…</span>
    </div>
  );
}

/** A grey block standing in for content that has not arrived yet. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-sunken', className)} />;
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-surface-sunken text-text-subtle">
        {icon ?? <Inbox aria-hidden className="size-5" />}
      </div>
      <p className="text-sm font-medium text-text">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * A failed request, shown with the server's own message.
 *
 * The request id is included because it is the thing that makes a support
 * conversation about a 500 tractable (§38).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null;
  const message =
    apiError?.message ?? (error instanceof Error ? error.message : 'Something went wrong.');

  return (
    <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-critical-soft text-critical-tone">
        <AlertTriangle aria-hidden className="size-5" />
      </div>
      <p className="text-sm font-medium text-text">{message}</p>
      {apiError?.requestId && (
        <p className="mt-1 font-mono text-[11px] text-text-subtle">Reference: {apiError.requestId}</p>
      )}
      {onRetry && (
        <Button className="mt-4" onClick={onRetry} size="sm">
          Try again
        </Button>
      )}
    </div>
  );
}

/** An inline banner for a failed mutation, shown beside the form that failed. */
export function InlineError({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Something went wrong.';

  return (
    <p
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-critical-border bg-critical-soft px-3 py-2',
        'text-xs text-critical-tone',
        className,
      )}
    >
      <AlertTriangle aria-hidden className="mt-px size-3.5 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

export function Callout({
  tone = 'info',
  icon,
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warning' | 'critical' | 'success';
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'border-info-border bg-info-soft text-info-tone',
    warning: 'border-warning-border bg-warning-soft text-warning-tone',
    critical: 'border-critical-border bg-critical-soft text-critical-tone',
    success: 'border-success-border bg-success-soft text-success-tone',
  } as const;

  return (
    <div className={cn('flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-xs', tones[tone], className)}>
      {icon && <span className="mt-px shrink-0">{icon}</span>}
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-0.5')}>{children}</div>}
      </div>
    </div>
  );
}
